import { Routes, Route, useLocation } from 'react-router-dom'
import { useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { tenantConfig } from './config/tenant'
import { organizationSchema, websiteSchema } from './config/seo'

import HomePage from './pages/HomePage'
import NotFoundPage from './pages/NotFoundPage'
import CalculatorPage from './pages/CalculatorPage'
import SavingsPage from './pages/SavingsPage'
import PropertyReportPage from './pages/PropertyReportPage'
import PlaybookPage from './pages/PlaybookPage'
import DebtRecyclingPage from './pages/DebtRecyclingPage'
import LoginPage from './pages/LoginPage'
import DataEntryPage from './pages/DataEntryPage'
import AboutPage from './pages/marketing/AboutPage'
import ServicesPage from './pages/marketing/ServicesPage'
import ContactPage from './pages/marketing/ContactPage'
import CalcLandingPage from './pages/marketing/CalcLandingPage'
import HardshipPage from './pages/HardshipPage'
import ComplaintsPage from './pages/ComplaintsPage'
import PrivacyPage from './pages/PrivacyPage'
import BlogPage from './pages/BlogPage'
import BlogPostPage from './pages/BlogPostPage'
import SuburbPage from './pages/SuburbPage'
import AssistWidget from './components/AssistWidget'
import ExitIntentModal from './components/ExitIntentModal'

/**
 * Root routing shell. Tenant CSS variables are injected here so any component
 * can read `var(--tenant-ink)` / `var(--tenant-accent)` without prop drilling.
 */
export default function App() {
  useEffect(() => {
    const root = document.documentElement
    root.style.setProperty('--tenant-ink', tenantConfig.primaryColor)
    root.style.setProperty('--tenant-accent', tenantConfig.accentColor)
    // Title is now managed declaratively by <Helmet> (default below + per-page <Seo>).
  }, [])

  const { pathname, hash } = useLocation()

  // Reset scroll to the top on every route change — otherwise navigating from a
  // footer link (bottom of the page) lands mid-way down the next page. Skipped
  // when a #hash target is present so the hash-scroll below can take over.
  useEffect(() => {
    if (hash) return
    window.scrollTo(0, 0)
  }, [pathname, hash])

  // Smooth-scroll to a #hash target after navigation (e.g. /services#faq from Learning Hub).
  useEffect(() => {
    if (!hash) return
    const el = document.getElementById(hash.slice(1))
    if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 50)
  }, [hash])

  return (
    <>
      {/* Site-wide defaults + business/website structured data. Per-page <Seo> overrides title/meta. */}
      <Helmet>
        <html lang="en-AU" />
        <title>{`${tenantConfig.name} · Mortgage Broking & Lending Advisory`}</title>
        <script type="application/ld+json">{JSON.stringify(organizationSchema())}</script>
        <script type="application/ld+json">{JSON.stringify(websiteSchema())}</script>
      </Helmet>
      <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/services" element={<ServicesPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/tools" element={<CalculatorPage />} />
      <Route path="/tools/savings" element={<SavingsPage />} />
      {tenantConfig.propertyReportEnabled && (
        <Route path="/tools/property-report" element={<PropertyReportPage />} />
      )}
      <Route path="/tools/:slug" element={<CalcLandingPage />} />
      <Route path="/playbook" element={<PlaybookPage />} />
      <Route path="/debt-recycling" element={<DebtRecyclingPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/suburbs/:state/:slug" element={<SuburbPage />} />
      <Route path="/hardship" element={<HardshipPage />} />
      <Route path="/complaints" element={<ComplaintsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/dataentry/login" element={<LoginPage />} />
      <Route path="/dataentry" element={<DataEntryPage />} />
      <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <AssistWidget />
      <ExitIntentModal />
    </>
  )
}
